import { computed, ref, type Ref } from "vue";
import { useCoolers } from "seedgreen-shared/queries/cooler";
import { useRanchSeasons } from "seedgreen-shared/queries/ranch-season";
import {
	getRanchIdsInRegion,
	getRegionCoolerIds,
	getRegionOptions,
	resolveSelectedRegion,
} from "@/views/Harvest/Berries/berriesRegions";

/**
 * Harvest region filter data for the Berries pages. Regions come from the coolers: a ranch is in
 * the region of the cooler its ranch season uses.
 * Only returns computeds, so it can be used in a Pinia setup store without adding state.
 * @param region - Selected region id, or null for all regions. May be a stale persisted id.
 * @param season - When set, a ranch's region is taken from its ranch season for that season only
 */
export function useBerriesRegions(
	region: Ref<number | null>,
	season: Ref<number | null> = ref(null),
) {
	// The source queries are used directly (not useHarvestOverviewRanches) because that derived
	// query resolves with an empty list before its sources load, so its pending state is unreliable
	const {
		coolers,
		isPending: areCoolersPending,
		isError: hasCoolersError,
	} = useCoolers();
	const {
		ranchSeasons,
		isPending: areRanchSeasonsPending,
		isError: hasRanchSeasonsError,
	} = useRanchSeasons();

	const isLoading = computed(
		() => areCoolersPending.value || areRanchSeasonsPending.value,
	);
	/** The region data failed to load, so whether the customer has regions is unknown */
	const hasLoadError = computed(
		() => hasCoolersError.value || hasRanchSeasonsError.value,
	);

	const regionOptions = computed(() =>
		getRegionOptions(coolers.value ?? [], ranchSeasons.value ?? []),
	);

	const selectedRegion = computed(() =>
		resolveSelectedRegion(
			region.value,
			regionOptions.value,
			isLoading.value,
		),
	);

	/** Coolers in the selected region, or null when no region is selected */
	const regionCoolerIds = computed(() => {
		const regionId = selectedRegion.value;
		if (regionId === null) return null;
		return getRegionCoolerIds(coolers.value ?? [], regionId);
	});

	/** Ranches in the selected region, or null when no region is selected */
	const ranchIdsInRegion = computed(() => {
		const coolerIds = regionCoolerIds.value;
		if (coolerIds === null) return null;
		return getRanchIdsInRegion(
			ranchSeasons.value ?? [],
			coolerIds,
			season.value,
		);
	});

	return {
		regionOptions,
		selectedRegion,
		regionCoolerIds,
		ranchIdsInRegion,
		isLoading,
		hasLoadError,
	};
}
